<template>
    <div class="spark-runner-game" :class="{ expanded: isExpanded, 'in-modal': inModal }">
        <button
            v-if="!inModal"
            type="button"
            class="game-toggle"
            @click.stop="gameExpanded = !gameExpanded"
            :aria-expanded="gameExpanded"
        >
            <font-awesome-icon :icon="['fas', gameExpanded ? 'chevron-down' : 'circle-play']" />
            {{ gameExpanded ? 'Hide game' : 'Play while you wait' }}
        </button>
        <transition name="slide">
            <div v-if="isExpanded" class="game-container">
                <div class="game-hud">
                    <div class="hud-block">
                        <span class="hud-number accent">{{ formattedScore }}</span>
                        <span class="hud-label">SCORE</span>
                    </div>
                    <div class="hud-block health-block">
                        <div class="health-bar">
                            <div class="health-fill" :style="healthBarStyle" />
                        </div>
                        <span class="hud-label">GRID HEALTH {{ Math.round(health) }}%</span>
                    </div>
                    <div class="hud-block">
                        <span class="hud-number">{{ formattedHighScore }}</span>
                        <span class="hud-label">BEST</span>
                    </div>
                </div>
                <div v-if="combo > 1 && gameState === 'playing'" class="combo-indicator">
                    &times;{{ combo }} COMBO
                </div>
                <div
                    ref="canvasWrapRef"
                    class="canvas-wrap"
                    tabindex="0"
                    @pointerdown.prevent="handlePointerDown"
                    @keydown="onKeydown"
                    aria-label="Frequency runner game — tap or Space to boost, ArrowDown to dive"
                >
                    <canvas ref="canvasRef" />
                    <div v-if="gameState === 'idle'" class="overlay">
                        <div class="overlay-card">
                            <h2 class="overlay-heading">&#9889; FREQUENCY RUNNER</h2>
                            <p class="overlay-sub">
                                Keep the grid at <strong>50 Hz</strong>. Navigate through market events.
                            </p>
                            <p class="overlay-sub controls-hint">
                                ↑ / Space / Tap = boost &nbsp;&nbsp; ↓ = dive
                            </p>
                            <p class="cta pulse-anim">TAP OR PRESS SPACE</p>
                        </div>
                    </div>
                    <div v-if="gameState === 'over'" class="overlay over-overlay">
                        <div class="overlay-card">
                            <h2 class="overlay-heading over-heading">&#9889; BLACKOUT</h2>
                            <div class="over-stats">
                                <div class="over-stat-item">
                                    <span class="over-stat-val">{{ formattedScore }}</span>
                                    <span class="over-stat-lbl">SCORE</span>
                                </div>
                                <div class="over-stat-item">
                                    <span class="over-stat-val">{{ eventsPassed }}</span>
                                    <span class="over-stat-lbl">EVENTS</span>
                                </div>
                            </div>
                            <p v-if="isNewBest" class="new-best-label">&#127942; NEW BEST!</p>
                            <p class="cta pulse-anim">TAP TO RETRY</p>
                        </div>
                    </div>
                </div>
                <div class="game-footer">
                    <button type="button" class="help-toggle-btn" @click.stop="showHelp = !showHelp">
                        <font-awesome-icon :icon="['fas', 'circle-info']" />
                        {{ showHelp ? 'Hide' : 'How to play' }}
                    </button>
                </div>
                <transition name="fade">
                    <div v-if="showHelp" class="help-content">
                        <p>
                            <strong>Goal:</strong> As System Operator, keep frequency in the green zone
                            (49.7–50.3 Hz) to prevent a blackout.
                        </p>
                        <p>
                            <strong>Controls:</strong> Tap, click, or press Space / ↑ to boost frequency
                            upward. Press ↓ to dive downward. Frequency naturally drifts down (demand increases).
                        </p>
                        <p>
                            <strong>Events:</strong> Market events scroll across as obstacles. Navigate
                            through the gap to pass safely — hitting a wall is instant blackout.
                        </p>
                        <p>
                            <strong>Scoring:</strong> Points accumulate faster the closer you are to 50 Hz.
                            Passing events earns bonus points with a combo multiplier.
                        </p>
                        <p>
                            <strong>Health:</strong> Drifting outside the green zone drains grid health.
                            Health recovers when you return to the zone. At 0% health — blackout!
                        </p>
                    </div>
                </transition>
            </div>
        </transition>
    </div>
</template>

<script setup lang="ts">
import {
    ref, computed, watch, onMounted, onBeforeUnmount, nextTick,
} from 'vue';

// ─── Constants ───────────────────────────────────────────
const FREQ_MIN = 49.0;
const FREQ_MAX = 51.0;
const FREQ_SAFE_LOW = 49.7;
const FREQ_SAFE_HIGH = 50.3;
const BASE_GRAVITY = 0.5;
const FLAP_VELOCITY = 0.8;
const DIVE_VELOCITY = -0.8;
const VELOCITY_DAMPING = 0.75;
const MAX_VELOCITY = 3.0;
const PLAYER_X_RATIO = 0.17;
const MAX_HEALTH = 100;
const HEALTH_DRAIN = 12;
const HEALTH_REGEN = 6;
const SCORE_PER_SEC = 12;
const SCORE_PER_EVENT = 25;
const STORAGE_KEY = 'spark-freq-runner-best';
const MAX_TRAIL = 180;
const STAR_COUNT = 35;
const MAX_PARTICLES = 60;
const COMBO_GRACE = 0.4;

const EVENT_LABELS = [
    'PLANT TRIP', 'HEATWAVE', 'WIND DROP', 'COLD SNAP',
    'DEMAND SURGE', 'GAS SHORT', 'SOLAR DIP', 'TV PICKUP',
    'CABLE FAULT', 'STORM',
];

// ─── Types ───────────────────────────────────────────────
interface GameEvent {
    id: number;
    x: number;
    gapCenter: number;
    gapHz: number;
    w: number;
    label: string;
    scored: boolean;
}
interface TrailPt { x: number; freq: number }
interface Particle {
    x: number; y: number; vx: number; vy: number;
    life: number; size: number; hue: number;
}
interface Star { x: number; y: number; r: number; a: number; s: number }

// ─── Props ────────────────────────────────────────────────
const props = withDefaults(
    defineProps<{ inModal?: boolean }>(),
    { inModal: false },
);

// ─── Reactive state ──────────────────────────────────────
const gameExpanded = ref(false);
const isExpanded = computed((): boolean => props.inModal || gameExpanded.value);
const gameState = ref<'idle' | 'playing' | 'over'>('idle');
const score = ref(0);
const health = ref(MAX_HEALTH);
const highScore = ref(0);
const combo = ref(1);
const eventsPassed = ref(0);
const isNewBest = ref(false);
const showHelp = ref(false);
const canvasWrapRef = ref<HTMLDivElement | null>(null);
const canvasRef = ref<HTMLCanvasElement | null>(null);

// ─── Computed ────────────────────────────────────────────
const formattedScore = computed((): string => score.value.toLocaleString());
const formattedHighScore = computed((): string => highScore.value.toLocaleString());
const healthBarStyle = computed((): Record<string, string> => {
    const h = health.value;
    let color = '#e93f6f';
    if (h > 60) color = '#00d282';
    else if (h > 30) color = '#f7a600';
    return { width: `${h}%`, backgroundColor: color };
});

// ─── Internal (non-reactive) state ───────────────────────
let freq = 50;
let vel = 0;
let rawScore = 0;
let animTime = 0;
let playTime = 0;
let lastTs = 0;
let animId: number | null = null;
let spawnTimer = 0;
let dmgFlash = 0;
let shake = 0;
let outsideTimer = 0;
let mounted = false;
let cw = 600;
let ch = 340;
let ctx: CanvasRenderingContext2D | null = null;
let resizeObs: ResizeObserver | null = null;
let nextId = 0;
let lastInputTs = 0;

const gev: GameEvent[] = [];
const trail: TrailPt[] = [];
const particles: Particle[] = [];
const stars: Star[] = [];

// ─── Helpers ─────────────────────────────────────────────
function freqToY (f: number): number {
    const pad = ch * 0.06;
    const t = (f - FREQ_MIN) / (FREQ_MAX - FREQ_MIN);
    return ch - pad - t * (ch - 2 * pad);
}

function dT (): number { return 1 - 1 / (1 + playTime / 60); }
function evSpeed (): number { return cw * (0.20 + dT() * 0.22); }
function gapHz (): number { return 0.65 - dT() * 0.25; }
function spawnInt (): number { return 3.0 - dT() * 1.8; }
function grav (): number { return BASE_GRAVITY + dT() * 0.45; }
function pRad (): number { return Math.max(8, ch / 24); }
function pX (): number { return cw * PLAYER_X_RATIO; }
function evW (): number { return Math.max(46, cw * 0.09); }

function loadBest (): void {
    try { highScore.value = Number(localStorage.getItem(STORAGE_KEY)) || 0; } catch { /* noop */ }
}
function saveBest (): void {
    try { localStorage.setItem(STORAGE_KEY, String(highScore.value)); } catch { /* noop */ }
}

// ─── Stars ───────────────────────────────────────────────
function initStars (): void {
    stars.length = 0;
    for (let i = 0; i < STAR_COUNT; i++) {
        stars.push({
            x: Math.random() * cw,
            y: Math.random() * ch,
            r: Math.random() * 1.2 + 0.3,
            a: Math.random() * 0.35 + 0.08,
            s: Math.random() * 18 + 6,
        });
    }
}

// ─── Particles ───────────────────────────────────────────
function emitSparks (x: number, y: number, n: number, hue = 35): void {
    for (let i = 0; i < n && particles.length < MAX_PARTICLES; i++) {
        particles.push({
            x,
            y,
            vx: (Math.random() - 0.5) * 120,
            vy: (Math.random() - 0.7) * 100,
            life: 1,
            size: Math.random() * 2.5 + 0.8,
            hue,
        });
    }
}

// ─── Spawning ────────────────────────────────────────────
function spawnEvent (): void {
    const gap = gapHz();
    const margin = gap / 2 + 0.15;
    const minC = FREQ_MIN + margin;
    const maxC = FREQ_MAX - margin;
    gev.push({
        id: nextId++,
        x: cw + 20,
        gapCenter: minC + Math.random() * (maxC - minC),
        gapHz: gap,
        w: evW(),
        label: EVENT_LABELS[Math.floor(Math.random() * EVENT_LABELS.length)],
        scored: false,
    });
}

// ─── Reset ───────────────────────────────────────────────
function resetGame (): void {
    freq = 50; vel = 0; rawScore = 0;
    playTime = 0; spawnTimer = 0; dmgFlash = 0;
    shake = 0; outsideTimer = 0;
    gev.length = 0; trail.length = 0; particles.length = 0;
    score.value = 0; health.value = MAX_HEALTH;
    combo.value = 1; eventsPassed.value = 0; isNewBest.value = false;
}

// ─── End game ────────────────────────────────────────────
function endGame (): void {
    if (gameState.value === 'over') return;
    gameState.value = 'over';
    dmgFlash = 0.5;
    shake = 6;
    if (score.value > highScore.value) {
        highScore.value = score.value;
        isNewBest.value = true;
        saveBest();
    }
}

// ─── Input ───────────────────────────────────────────────
function doFlap (): void {
    vel = FLAP_VELOCITY * (vel > 0 ? VELOCITY_DAMPING : 1);
    emitSparks(pX(), freqToY(freq), 6);
}

function doDive (): void {
    vel = DIVE_VELOCITY * (vel < 0 ? VELOCITY_DAMPING : 1);
    emitSparks(pX(), freqToY(freq), 4, 200);
}

function onInput (direction: 'up' | 'down' = 'up'): void {
    const now = performance.now();
    if (now - lastInputTs < 50) return;
    lastInputTs = now;
    canvasWrapRef.value?.focus();

    if (gameState.value === 'over') { resetGame(); gameState.value = 'playing'; doFlap(); return; }
    if (gameState.value === 'idle') { resetGame(); gameState.value = 'playing'; doFlap(); return; }
    if (direction === 'down') doDive();
    else doFlap();
}

function handlePointerDown (): void {
    onInput('up');
}

function onKeydown (e: KeyboardEvent): void {
    if (e.code === 'Space' || e.code === 'ArrowUp') {
        e.preventDefault();
        onInput('up');
    } else if (e.code === 'ArrowDown') {
        e.preventDefault();
        onInput('down');
    }
}

function handleGlobalKey (e: KeyboardEvent): void {
    if (!isExpanded.value) return;
    if (e.code !== 'Space' && e.code !== 'ArrowUp' && e.code !== 'ArrowDown') return;
    const el = e.target as HTMLElement;
    if (el?.tagName === 'INPUT' || el?.tagName === 'TEXTAREA' || el?.tagName === 'SELECT' || el?.isContentEditable) return;
    if (canvasWrapRef.value && document.activeElement === canvasWrapRef.value) return;
    e.preventDefault();
    onInput(e.code === 'ArrowDown' ? 'down' : 'up');
}

// ─── Update ──────────────────────────────────────────────
function updatePhysics (dt: number): void {
    vel -= grav() * dt;
    vel = Math.max(-MAX_VELOCITY, Math.min(MAX_VELOCITY, vel));
    freq += vel * dt;
    if (freq <= FREQ_MIN) { freq = FREQ_MIN; if (vel < 0) vel *= -0.25; }
    if (freq >= FREQ_MAX) { freq = FREQ_MAX; if (vel > 0) vel *= -0.25; }
}

function updateHealth (dt: number): void {
    const inZone = freq >= FREQ_SAFE_LOW && freq <= FREQ_SAFE_HIGH;
    if (inZone) {
        health.value = Math.min(MAX_HEALTH, health.value + HEALTH_REGEN * dt);
        outsideTimer = 0;
    } else {
        const dist = freq < FREQ_SAFE_LOW ? FREQ_SAFE_LOW - freq : freq - FREQ_SAFE_HIGH;
        const sev = Math.min(dist / 0.5, 1);
        health.value -= HEALTH_DRAIN * (0.4 + 0.6 * sev) * dt;
        outsideTimer += dt;
        if (outsideTimer > COMBO_GRACE && combo.value > 1) combo.value = 1;
        if (health.value <= 0) { health.value = 0; endGame(); }
    }
}

function updateScore (dt: number): void {
    const dist = Math.abs(freq - 50);
    const factor = Math.max(0, 1 - dist / 0.5);
    rawScore += factor * factor * SCORE_PER_SEC * dt;
    const rounded = Math.round(rawScore);
    if (rounded !== score.value) score.value = rounded;
}

function updateTrail (dt: number): void {
    const speed = evSpeed();
    for (let i = 0; i < trail.length; i++) trail[i].x -= speed * dt;
    trail.push({ x: pX(), freq });
    while (trail.length > MAX_TRAIL) trail.shift();
    while (trail.length > 0 && trail[0].x < -10) trail.shift();
}

function updateEvents (dt: number): void {
    const speed = evSpeed();
    const px = pX();
    const pr = pRad() * 0.55;

    spawnTimer += dt;
    if (spawnTimer >= spawnInt()) { spawnTimer = 0; spawnEvent(); }

    for (let i = gev.length - 1; i >= 0; i--) {
        const ev = gev[i];
        ev.x -= speed * dt;
        if (ev.x + ev.w < -20) { gev.splice(i, 1); continue; } // eslint-disable-line no-continue

        const hOvr = px + pr > ev.x && px - pr < ev.x + ev.w;
        if (hOvr) {
            const halfGap = ev.gapHz / 2 + 0.025;
            if (freq < ev.gapCenter - halfGap || freq > ev.gapCenter + halfGap) {
                endGame();
                return;
            }
        }
        if (!ev.scored && ev.x + ev.w < px - pr) {
            ev.scored = true;
            rawScore += SCORE_PER_EVENT * combo.value;
            score.value = Math.round(rawScore);
            eventsPassed.value++;
            combo.value++;
            emitSparks(ev.x + ev.w, freqToY(ev.gapCenter), 10, 120);
        }
    }
}

function updateParticles (dt: number): void {
    for (let i = particles.length - 1; i >= 0; i--) {
        const p = particles[i];
        p.x += p.vx * dt;
        p.y += p.vy * dt;
        p.vy += 60 * dt;
        p.life -= dt * 2.5;
        if (p.life <= 0) particles.splice(i, 1);
    }
}

function updateEffects (dt: number): void {
    if (dmgFlash > 0) dmgFlash = Math.max(0, dmgFlash - dt * 3);
    if (shake > 0) shake = Math.max(0, shake - dt * 12);
}

// ─── Drawing ─────────────────────────────────────────────
function draw (): void {
    if (!ctx) return;
    ctx.save();
    if (shake > 0) ctx.translate((Math.random() - 0.5) * shake, (Math.random() - 0.5) * shake);

    const bg = ctx.createLinearGradient(0, 0, 0, ch);
    bg.addColorStop(0, '#0B1120');
    bg.addColorStop(0.5, '#131D2E');
    bg.addColorStop(1, '#0D1825');
    ctx.fillStyle = bg;
    ctx.fillRect(0, 0, cw, ch);

    drawStars();
    drawGrid();
    drawSafeZone();
    draw50Hz();
    drawHzLabels();

    if (gameState.value === 'playing') {
        drawTrail();
        drawGameEvents();
        drawWarnings();
        drawPlayer();
        drawFreqReadout();
    } else if (gameState.value === 'idle') {
        drawPlayer();
    } else {
        drawTrail();
        drawGameEvents();
        drawPlayer();
    }

    drawParticles();
    drawDamageFlash();
    drawVignette();
    drawScanlines();
    ctx.restore();
}

function drawStars (): void {
    if (!ctx) return;
    for (let i = 0; i < stars.length; i++) {
        const s = stars[i];
        ctx.fillStyle = `rgba(180,210,240,${s.a})`;
        ctx.beginPath();
        ctx.arc(s.x, s.y, s.r, 0, Math.PI * 2);
        ctx.fill();
    }
}

function drawGrid (): void {
    if (!ctx) return;
    ctx.strokeStyle = 'rgba(100,180,255,0.035)';
    ctx.lineWidth = 0.5;
    for (let f = Math.ceil(FREQ_MIN * 10) / 10; f <= FREQ_MAX; f += 0.1) {
        const y = freqToY(f);
        ctx.beginPath(); ctx.moveTo(0, y); ctx.lineTo(cw, y); ctx.stroke();
    }
    const sp = cw / 10;
    for (let x = sp; x < cw; x += sp) {
        ctx.beginPath(); ctx.moveTo(x, 0); ctx.lineTo(x, ch); ctx.stroke();
    }
}

function drawSafeZone (): void {
    if (!ctx) return;
    const top = freqToY(FREQ_SAFE_HIGH);
    const bot = freqToY(FREQ_SAFE_LOW);
    ctx.fillStyle = 'rgba(0,210,130,0.07)';
    ctx.fillRect(0, top, cw, bot - top);
    ctx.strokeStyle = 'rgba(0,210,130,0.45)';
    ctx.lineWidth = 1.5;
    ctx.setLineDash([]);
    ctx.beginPath(); ctx.moveTo(0, top); ctx.lineTo(cw, top); ctx.stroke();
    ctx.beginPath(); ctx.moveTo(0, bot); ctx.lineTo(cw, bot); ctx.stroke();
}

function draw50Hz (): void {
    if (!ctx) return;
    const y = freqToY(50);
    ctx.strokeStyle = 'rgba(0,210,130,0.18)';
    ctx.lineWidth = 1;
    ctx.setLineDash([6, 5]);
    ctx.beginPath(); ctx.moveTo(0, y); ctx.lineTo(cw, y); ctx.stroke();
    ctx.setLineDash([]);
}

function drawHzLabels (): void {
    if (!ctx) return;
    const fs = Math.max(9, Math.min(14, ch / 28));
    ctx.textAlign = 'left';
    ctx.textBaseline = 'middle';
    const labels: Array<[number, string, boolean]> = [
        [51.0, '51.0', false], [50.5, '50.5', false],
        [50.0, '50.0', true],
        [49.5, '49.5', false], [49.0, '49.0', false],
    ];
    for (let i = 0; i < labels.length; i++) {
        const [hz, text, center] = labels[i];
        ctx.fillStyle = center ? 'rgba(0,210,130,0.65)' : 'rgba(180,210,240,0.35)';
        ctx.font = `${center ? 'bold ' : ''}${fs}px system-ui,sans-serif`;
        ctx.fillText(`${text} Hz`, 6, freqToY(hz));
    }
}

function drawTrail (): void {
    if (!ctx || trail.length < 2) return;
    ctx.save();
    ctx.beginPath();
    ctx.moveTo(trail[0].x, freqToY(trail[0].freq));
    for (let i = 1; i < trail.length; i++) ctx.lineTo(trail[i].x, freqToY(trail[i].freq));
    const last = trail[trail.length - 1];
    const grad = ctx.createLinearGradient(trail[0].x, 0, last.x, 0);
    grad.addColorStop(0, 'rgba(243,146,0,0)');
    grad.addColorStop(0.4, 'rgba(243,146,0,0.25)');
    grad.addColorStop(1, 'rgba(243,146,0,0.7)');
    ctx.strokeStyle = grad;
    ctx.lineWidth = 2.5;
    ctx.shadowColor = '#f39200';
    ctx.shadowBlur = 8;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    ctx.stroke();
    ctx.restore();
}

function drawGameEvents (): void {
    if (!ctx) return;
    for (let i = 0; i < gev.length; i++) {
        const ev = gev[i];
        if (ev.x > cw + 20 || ev.x + ev.w < -20) continue; // eslint-disable-line no-continue
        const gTop = freqToY(ev.gapCenter + ev.gapHz / 2);
        const gBot = freqToY(ev.gapCenter - ev.gapHz / 2);

        ctx.save();
        ctx.shadowColor = 'rgba(220,50,60,0.25)';
        ctx.shadowBlur = 12;
        const tg = ctx.createLinearGradient(ev.x, 0, ev.x + ev.w, 0);
        tg.addColorStop(0, 'rgba(180,40,50,0.82)');
        tg.addColorStop(0.5, 'rgba(210,55,65,0.88)');
        tg.addColorStop(1, 'rgba(180,40,50,0.82)');
        ctx.fillStyle = tg;
        ctx.fillRect(ev.x, 0, ev.w, gTop);
        ctx.fillRect(ev.x, gBot, ev.w, ch - gBot);
        ctx.restore();

        ctx.fillStyle = 'rgba(255,110,110,0.6)';
        ctx.fillRect(ev.x, gTop - 2, ev.w, 2);
        ctx.fillRect(ev.x, gBot, ev.w, 2);

        ctx.fillStyle = 'rgba(0,210,130,0.04)';
        ctx.fillRect(ev.x, gTop, ev.w, gBot - gTop);

        ctx.strokeStyle = 'rgba(255,110,110,0.3)';
        ctx.lineWidth = 1;
        ctx.beginPath();
        ctx.moveTo(ev.x, 0); ctx.lineTo(ev.x, gTop);
        ctx.moveTo(ev.x + ev.w, 0); ctx.lineTo(ev.x + ev.w, gTop);
        ctx.moveTo(ev.x, gBot); ctx.lineTo(ev.x, ch);
        ctx.moveTo(ev.x + ev.w, gBot); ctx.lineTo(ev.x + ev.w, ch);
        ctx.stroke();

        const lfs = Math.max(7, Math.min(11, ch / 36));
        ctx.fillStyle = 'rgba(255,255,255,0.65)';
        ctx.font = `bold ${lfs}px system-ui,sans-serif`;
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';
        ctx.fillText(ev.label, ev.x + ev.w / 2, (gTop + gBot) / 2);
    }
}

function drawWarnings (): void {
    if (!ctx) return;
    for (let i = 0; i < gev.length; i++) {
        const ev = gev[i];
        if (ev.x <= cw || ev.x > cw + 160) continue; // eslint-disable-line no-continue
        const y = freqToY(ev.gapCenter);
        const prox = 1 - (ev.x - cw) / 160;
        const blink = Math.sin(animTime * 10) > 0 ? 1 : 0.4;
        ctx.save();
        ctx.globalAlpha = prox * blink;
        ctx.strokeStyle = '#e93f6f';
        ctx.lineWidth = 2;
        const s = 6;
        ctx.beginPath();
        ctx.moveTo(cw - 4, y - s);
        ctx.lineTo(cw - 4 - s, y);
        ctx.lineTo(cw - 4, y + s);
        ctx.stroke();
        ctx.restore();
    }
}

function drawPlayer (): void {
    if (!ctx) return;
    const px = pX();
    const py = freqToY(freq);
    const r = pRad();
    const inZone = freq >= FREQ_SAFE_LOW && freq <= FREQ_SAFE_HIGH;
    const pulse = 1 + Math.sin(animTime * 5) * 0.07;

    const glowR = r * 2.2 * pulse;
    const glowCol = inZone ? 'rgba(243,146,0,' : 'rgba(233,63,111,';
    const glow = ctx.createRadialGradient(px, py, r * 0.3, px, py, glowR);
    glow.addColorStop(0, `${glowCol}0.30)`);
    glow.addColorStop(1, `${glowCol}0)`);
    ctx.fillStyle = glow;
    ctx.beginPath(); ctx.arc(px, py, glowR, 0, Math.PI * 2); ctx.fill();

    ctx.save();
    ctx.shadowColor = '#f39200';
    ctx.shadowBlur = 14;
    ctx.fillStyle = '#f39200';
    ctx.beginPath(); ctx.arc(px, py, r, 0, Math.PI * 2); ctx.fill();
    ctx.restore();

    const hl = ctx.createRadialGradient(px - r * 0.25, py - r * 0.25, 0, px, py, r);
    hl.addColorStop(0, 'rgba(255,220,150,0.55)');
    hl.addColorStop(0.6, 'rgba(243,146,0,0.15)');
    hl.addColorStop(1, 'rgba(243,146,0,0)');
    ctx.fillStyle = hl;
    ctx.beginPath(); ctx.arc(px, py, r, 0, Math.PI * 2); ctx.fill();

    const b = r * 0.42;
    ctx.fillStyle = '#fff';
    ctx.beginPath();
    ctx.moveTo(px - b * 0.15, py - b);
    ctx.lineTo(px + b * 0.45, py - b * 0.05);
    ctx.lineTo(px, py + b * 0.05);
    ctx.lineTo(px + b * 0.15, py + b);
    ctx.lineTo(px - b * 0.45, py + b * 0.05);
    ctx.lineTo(px, py - b * 0.05);
    ctx.closePath();
    ctx.fill();
}

function drawFreqReadout (): void {
    if (!ctx) return;
    const px = pX();
    const py = freqToY(freq);
    const r = pRad();
    const fs = Math.max(9, Math.min(13, ch / 30));
    ctx.fillStyle = 'rgba(243,146,0,0.75)';
    ctx.font = `bold ${fs}px monospace`;
    ctx.textAlign = 'left';
    ctx.textBaseline = 'middle';
    ctx.fillText(`${freq.toFixed(2)} Hz`, px + r + 8, py);
}

function drawParticles (): void {
    if (!ctx) return;
    for (let i = 0; i < particles.length; i++) {
        const p = particles[i];
        const a = Math.max(0, p.life);
        ctx.fillStyle = `hsla(${p.hue},90%,60%,${a})`;
        ctx.beginPath();
        ctx.arc(p.x, p.y, p.size * a, 0, Math.PI * 2);
        ctx.fill();
    }
}

function drawDamageFlash (): void {
    if (!ctx || dmgFlash <= 0) return;
    ctx.fillStyle = `rgba(220,30,50,${dmgFlash * 0.35})`;
    ctx.fillRect(0, 0, cw, ch);
}

function drawVignette (): void {
    if (!ctx) return;
    const cx = cw / 2;
    const cy = ch / 2;
    const r = Math.max(cw, ch) * 0.75;
    const g = ctx.createRadialGradient(cx, cy, r * 0.45, cx, cy, r);
    g.addColorStop(0, 'rgba(0,0,0,0)');
    g.addColorStop(1, 'rgba(0,0,0,0.4)');
    ctx.fillStyle = g;
    ctx.fillRect(0, 0, cw, ch);
}

function drawScanlines (): void {
    if (!ctx) return;
    ctx.fillStyle = 'rgba(0,0,0,0.025)';
    for (let y = 0; y < ch; y += 3) ctx.fillRect(0, y, cw, 1);
}

// ─── Main loop ───────────────────────────────────────────
function loop (ts: number): void {
    if (!mounted || !ctx) return;
    const dt = Math.min((ts - lastTs) / 1000, 0.1) || 0.016;
    lastTs = ts;
    animTime += dt;

    for (let si = 0; si < stars.length; si++) {
        const s = stars[si];
        s.x -= s.s * dt;
        if (s.x < -5) { s.x = cw + 5; s.y = Math.random() * ch; }
    }
    updateParticles(dt);
    updateEffects(dt);

    if (gameState.value === 'playing') {
        playTime += dt;
        updatePhysics(dt);
        updateHealth(dt);
    }
    if (gameState.value === 'playing') {
        updateScore(dt);
        updateTrail(dt);
        updateEvents(dt);
    }
    if (gameState.value === 'idle') {
        freq = 50 + Math.sin(animTime * 1.8) * 0.12;
    }

    draw();
    animId = requestAnimationFrame(loop);
}

function startLoop (): void {
    if (animId != null) return;
    lastTs = performance.now();
    animId = requestAnimationFrame(loop);
}

function stopLoop (): void {
    if (animId != null) { cancelAnimationFrame(animId); animId = null; }
}

// ─── Canvas sizing ───────────────────────────────────────
function resizeCanvas (): void {
    if (!canvasWrapRef.value || !canvasRef.value) return;
    const rect = canvasWrapRef.value.getBoundingClientRect();
    const w = rect.width;
    if (w < 1) return;
    const h = Math.min(Math.max(w * 0.45, 240), 500);
    cw = w;
    ch = h;
    const dpr = window.devicePixelRatio || 1;
    canvasRef.value.width = Math.round(w * dpr);
    canvasRef.value.height = Math.round(h * dpr);
    canvasRef.value.style.height = `${h}px`;
    ctx = canvasRef.value.getContext('2d');
    if (ctx) ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    initStars();
}

// ─── Lifecycle ───────────────────────────────────────────
watch(isExpanded, (expanded: boolean): void => {
    if (expanded) {
        nextTick(() => {
            resizeCanvas();
            if (canvasWrapRef.value && !resizeObs) {
                resizeObs = new ResizeObserver(resizeCanvas);
                resizeObs.observe(canvasWrapRef.value);
            }
            canvasWrapRef.value?.focus();
            startLoop();
        });
    } else {
        stopLoop();
        resizeObs?.disconnect();
        resizeObs = null;
    }
});

onMounted((): void => {
    mounted = true;
    loadBest();
    window.addEventListener('keydown', handleGlobalKey);
    if (isExpanded.value) {
        nextTick(() => {
            resizeCanvas();
            if (canvasWrapRef.value) {
                resizeObs = new ResizeObserver(resizeCanvas);
                resizeObs.observe(canvasWrapRef.value);
            }
            startLoop();
        });
    }
});

onBeforeUnmount((): void => {
    mounted = false;
    stopLoop();
    window.removeEventListener('keydown', handleGlobalKey);
    resizeObs?.disconnect();
});
</script>

<style lang="scss" scoped>
.spark-runner-game {
    width: 100%;
}

.game-toggle {
    display: inline-flex;
    align-items: center;
    gap: 0.5rem;
    padding: 0.6rem 1.2rem;
    font-size: 0.95rem;
    font-weight: 600;
    color: var(--accentColour);
    background: transparent;
    border: 1px solid var(--neutral300);
    border-radius: var(--borderRadiusOuter);
    cursor: pointer;
    transition: background-color 0.2s, border-color 0.2s;

    &:hover {
        background: var(--neutral200);
        border-color: var(--neutral400);
    }
}

.game-container {
    margin-top: 0.75rem;
    border-radius: var(--borderRadiusOuter);
    border: 1px solid rgba(100, 160, 255, 0.12);
    background: linear-gradient(180deg, #0e1726 0%, #141f30 100%);
    overflow: hidden;
}

.spark-runner-game.in-modal .game-container {
    margin-top: 0;
    border-radius: var(--borderRadiusInner);
}

.spark-runner-game.in-modal .game-hud {
    padding: 1rem 1.25rem 0.6rem;
}

.game-hud {
    display: flex;
    align-items: flex-end;
    justify-content: space-between;
    gap: 0.75rem;
    padding: 0.9rem 1rem 0.5rem;
    flex-wrap: wrap;
}

.hud-block {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 0.15rem;
    min-width: 56px;
}

.hud-number {
    font-size: clamp(1.1rem, 3.5vw, 1.5rem);
    font-weight: 800;
    letter-spacing: -0.02em;
    color: rgba(255, 255, 255, 0.85);
    font-variant-numeric: tabular-nums;

    &.accent {
        color: #f39200;
    }
}

.spark-runner-game.in-modal .hud-number {
    font-size: var(--largeFontSize);
}

.hud-label {
    font-size: clamp(0.55rem, 1.5vw, 0.65rem);
    font-weight: 700;
    letter-spacing: 0.08em;
    text-transform: uppercase;
    color: rgba(180, 210, 240, 0.45);
}

.spark-runner-game.in-modal .hud-label {
    font-size: var(--mediumFontSize);
}

.health-block {
    flex: 1;
    min-width: 100px;
    max-width: 280px;
}

.health-bar {
    width: 100%;
    height: 10px;
    border-radius: 5px;
    background: rgba(255, 255, 255, 0.08);
    overflow: hidden;
}

.health-fill {
    height: 100%;
    border-radius: 5px;
    transition: width 0.12s ease, background-color 0.25s ease;
}

.combo-indicator {
    text-align: center;
    font-size: clamp(0.75rem, 2vw, 0.9rem);
    font-weight: 800;
    color: #ffc857;
    letter-spacing: 0.05em;
    padding: 0 1rem 0.3rem;
    text-shadow: 0 0 8px rgba(255, 200, 87, 0.5);
}

.spark-runner-game.in-modal .combo-indicator {
    font-size: var(--mediumFontSize);
}

.canvas-wrap {
    position: relative;
    width: 100%;
    outline: none;
    cursor: pointer;
    touch-action: none;
    -webkit-tap-highlight-color: transparent;
    user-select: none;

    canvas {
        display: block;
        width: 100%;
    }

    &:focus-visible {
        box-shadow: inset 0 0 0 2px var(--accentColour, #f39200);
    }
}

.overlay {
    position: absolute;
    inset: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    background: rgba(5, 10, 22, 0.72);
    backdrop-filter: blur(3px);
    z-index: 5;
}

.over-overlay {
    background: rgba(25, 5, 10, 0.78);
}

.overlay-card {
    text-align: center;
    color: #fff;
    padding: 1.5rem 2rem;
    max-width: 90%;
}

.spark-runner-game.in-modal .overlay-card {
    padding: 2rem 2.5rem;
}

.overlay-heading {
    font-size: clamp(1.2rem, 4vw, 1.8rem);
    font-weight: 900;
    letter-spacing: 0.04em;
    margin: 0 0 0.6rem;
    color: #f39200;
    text-shadow: 0 0 20px rgba(243, 146, 0, 0.5);

    &.over-heading {
        color: #e93f6f;
        text-shadow: 0 0 20px rgba(233, 63, 111, 0.5);
    }
}

.spark-runner-game.in-modal .overlay-heading {
    font-size: var(--largeFontSize);
    margin-bottom: 0.75rem;
}

.overlay-sub {
    font-size: clamp(0.85rem, 2.5vw, 1.05rem);
    color: rgba(200, 220, 240, 0.75);
    margin: 0 0 1.2rem;
    line-height: 1.5;

    strong {
        color: #00d282;
    }
}

.spark-runner-game.in-modal .overlay-sub {
    font-size: var(--mediumFontSize);
    margin-bottom: 1rem;
}

.controls-hint {
    font-size: clamp(0.7rem, 2vw, 0.85rem);
    margin-bottom: 0.8rem;
    letter-spacing: 0.04em;
    opacity: 0.7;
}

.spark-runner-game.in-modal .controls-hint {
    font-size: var(--mediumFontSize);
}

.cta {
    font-size: clamp(0.78rem, 2.2vw, 0.95rem);
    font-weight: 700;
    letter-spacing: 0.1em;
    color: rgba(255, 255, 255, 0.65);
    margin: 0;
}

.spark-runner-game.in-modal .cta {
    font-size: var(--mediumFontSize);
}

.over-stats {
    display: flex;
    gap: 2rem;
    justify-content: center;
    margin: 1rem 0;
}

.over-stat-item {
    text-align: center;
}

.over-stat-val {
    display: block;
    font-size: clamp(1.4rem, 5vw, 2.2rem);
    font-weight: 900;
    color: #fff;
    font-variant-numeric: tabular-nums;
}

.spark-runner-game.in-modal .over-stat-val {
    font-size: var(--largeFontSize);
}

.over-stat-lbl {
    font-size: clamp(0.55rem, 1.5vw, 0.65rem);
    font-weight: 700;
    letter-spacing: 0.1em;
    color: rgba(180, 210, 240, 0.45);
}

.spark-runner-game.in-modal .over-stat-lbl {
    font-size: var(--mediumFontSize);
}

.new-best-label {
    font-size: clamp(0.9rem, 2.5vw, 1.1rem);
    font-weight: 800;
    color: #ffc857;
    margin: 0.5rem 0;
    text-shadow: 0 0 12px rgba(255, 200, 87, 0.6);
}

.spark-runner-game.in-modal .new-best-label {
    font-size: var(--mediumFontSize);
}

.pulse-anim {
    animation: pulse 1.8s ease-in-out infinite;
}

@keyframes pulse {
    0%, 100% { opacity: 0.55; }
    50% { opacity: 1; }
}

.game-footer {
    padding: 0.5rem 1rem;
    display: flex;
    justify-content: flex-end;
}

.help-toggle-btn {
    display: inline-flex;
    align-items: center;
    gap: 0.35rem;
    padding: 0.35rem 0.7rem;
    font-size: clamp(0.72rem, 1.8vw, 0.82rem);
    color: rgba(180, 210, 240, 0.5);
    background: transparent;
    border: none;
    border-radius: var(--borderRadiusInner, 6px);
    cursor: pointer;
    transition: color 0.2s, background 0.2s;

    &:hover {
        color: #f39200;
        background: rgba(255, 255, 255, 0.05);
    }
}

.spark-runner-game.in-modal .help-toggle-btn {
    font-size: var(--mediumFontSize);
    padding: 0.5rem 0.9rem;
}

.help-content {
    padding: 0.6rem 1.2rem 1rem;
    font-size: clamp(0.78rem, 2vw, 0.9rem);
    color: rgba(200, 220, 240, 0.7);
    line-height: 1.6;
    border-top: 1px solid rgba(100, 160, 255, 0.08);

    p {
        margin: 0 0 0.5rem;

        &:last-child {
            margin-bottom: 0;
        }
    }

    strong {
        color: rgba(255, 255, 255, 0.85);
    }
}

.spark-runner-game.in-modal .help-content {
    padding: 0.9rem 1.25rem 1.25rem;
    font-size: var(--mediumFontSize);

    p {
        margin-bottom: 0.6rem;
    }
}

.slide-enter-active,
.slide-leave-active {
    transition: opacity 0.25s ease, transform 0.25s ease;
}

.slide-enter-from,
.slide-leave-to {
    opacity: 0;
    transform: translateY(-6px);
}

.slide-enter-to,
.slide-leave-from {
    opacity: 1;
    transform: translateY(0);
}

.fade-enter-active,
.fade-leave-active {
    transition: opacity 0.2s ease;
}

.fade-enter-from,
.fade-leave-to {
    opacity: 0;
}

@media (max-width: 480px) {
    .game-hud {
        padding: 0.6rem 0.6rem 0.4rem;
        gap: 0.4rem;
    }

    .health-block {
        min-width: 80px;
    }

    .over-stats {
        gap: 1rem;
    }
}
</style>
